import React, { useMemo } from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { Breadcrumbs, Typography, Icon, Box } from "@mui/material";
import routes from "./navBar/roots/routes";
import useSmoothResponsiveFontSize from "../../../_hook/useSmoothResponsiveFontSize";

// Utility functions
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const isValidObjectId = (id) => /^[a-fA-F0-9]{24}$/.test(id);

// Memoized maps outside of component to prevent recreation
const iconMap = routes.reduce(
  (acc, route) => {
    acc[route.routeName] = route.icon || "default_icon";
    return acc;
  },
  { home: "home" }
);

const nameMap = routes.reduce(
  (acc, route) => {
    acc[route.routeName] = route.name || capitalize(route.routeName);
    return acc;
  },
  { home: "Home" }
);

const IconBreadcrumbs = () => {
  const { pathname } = useLocation();
  const customfontsize = useSmoothResponsiveFontSize();
  const pathSegments = useMemo(
    () =>
      pathname
        .split("/")
        .filter(Boolean)
        .map((segment) => segment.split("?")[0]),
    [pathname]
  );

  const filteredSegments = useMemo(() => {
    if (!pathSegments.length) return [];
    const lastSegment = pathSegments[pathSegments.length - 1];
    return isValidObjectId(lastSegment)
      ? pathSegments.slice(0, -1)
      : pathSegments;
  }, [pathSegments]);

  const breadcrumbItems = useMemo(() => {
    let pathAccumulator = "";
    return filteredSegments.map((segment, index) => {
      const isLast = index === filteredSegments.length - 1;
      const matchingRoute = routes.find(
        (route) => route.route === `/${segment}` || route.routeName === segment
      );
      const label =
        matchingRoute?.name || capitalize(segment.replace(/-/g, " "));
      const iconName = matchingRoute?.routeName
        ? iconMap[matchingRoute.routeName]
        : iconMap.default;
      pathAccumulator += `/${segment}`;

      return isLast ? (
        <Box
          key={pathAccumulator}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Icon color="primary" style={{fontSize:`${customfontsize}px`}}>{iconName}</Icon>
          <Typography  sx={{ pl: 1 ,fontSize:`${customfontsize}px`}}>{label}</Typography>
        </Box>
      ) : (
        <Box
          key={pathAccumulator}
          component={RouterLink}
          to={pathAccumulator}
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <Icon color="primary" style={{fontSize:`${customfontsize}px`}}>{iconName}</Icon>
          <Typography  sx={{ pl: 1 ,fontSize:`${customfontsize}px`}}>{label}</Typography>
        </Box>
      );
    });
  }, [filteredSegments,customfontsize]);
  if (pathSegments.length <= 0) return null;
  return (
    <Box sx={{ py: 1, m: 0 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ p: 0, m: 0 }}>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <Icon color="primary" style={{fontSize:`${customfontsize}px`}}>{iconMap.home}</Icon>
          <Typography sx={{ pl: 1 ,fontSize:`${customfontsize}px`}}>{nameMap.home}</Typography>
        </Box>
        {breadcrumbItems}
      </Breadcrumbs>
    </Box>
  );
};

export default IconBreadcrumbs;
